import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Event } from "@/types";
import { EventCardPrimary } from "@components/molecules";

interface CardSliderProps {
  events: Event[];
}

/**
 * Card slider organism.
 * Displays a horizontal carousel of primary event cards with fade effect on edges.
 * Includes dot indicators for current position and infinite loop via virtual cloning.
 */
export function CardSlider({ events }: CardSliderProps) {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showEdgePadding, setShowEdgePadding] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isJumping = useRef(false);

  // Mouse drag state
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);
  const dragMoved = useRef(false);

  // Auto-play state
  const isHovered = useRef(false);
  const activeIndexRef = useRef(activeIndex);
  const AUTO_PLAY_MS = 4000;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    isDragging.current = true;
    dragMoved.current = false;
    dragStartX.current = e.clientX;
    dragStartScrollLeft.current = sliderRef.current.scrollLeft;
    sliderRef.current.style.cursor = "grabbing";
    sliderRef.current.style.userSelect = "none";
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !sliderRef.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 4) dragMoved.current = true;
    sliderRef.current.scrollLeft = dragStartScrollLeft.current - dx;
  };

  const handleMouseUp = () => {
    if (!sliderRef.current) return;
    isDragging.current = false;
    sliderRef.current.style.cursor = "grab";
    sliderRef.current.style.userSelect = "";
  };

  const cardWidth = 300;
  const gap = 67;
  const totalSlides = events.length;

  /**
   * Calculate cloned items count for infinite loop:
   * - Ensure at least 1 (handle empty state)
   * - Cap at 3 (minimum viable for seamless wrapping)
   * This creates virtual array: [tail clones, ...events, head clones]
   */
  const cloneCount = useMemo(
    () => Math.min(Math.max(totalSlides, 1), 3),
    [totalSlides],
  );

  // Build virtual array: [tail clones, ...events, head clones]
  const renderItems = useMemo(() => {
    if (totalSlides === 0) return [];
    const tail = events.slice(-cloneCount);
    const head = events.slice(0, cloneCount);
    return [...tail, ...events, ...head];
  }, [events, cloneCount, totalSlides]);

  // Offset for real items in the rendered array
  const itemOffset = cloneCount;

  /**
   * Scroll to a specific slide by index with optional smooth animation.
   * Accounts for virtual clones (offset) to position the real item at center-view.
   * @param index - Real slide index (0-based, ignores clones)
   * @param smooth - Whether to animate scroll (default: true)
   */
  const scrollToIndex = useCallback(
    (index: number, smooth = true) => {
      if (!sliderRef.current) return;
      const container = sliderRef.current;
      const containerWidth = container.offsetWidth;

      // Calculate position using the real index + offset for virtual clones
      const renderIndex = index + itemOffset;
      const scrollPos =
        renderIndex * (cardWidth + gap) - (containerWidth / 2 - cardWidth / 2);

      container.scrollTo({
        left: scrollPos,
        behavior: smooth ? "smooth" : "auto",
      });
      setActiveIndex(index);
    },
    [cardWidth, gap, itemOffset],
  );

  // Keep ref in sync with state to avoid stale closure in setInterval
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // Center initial card on mount
  useEffect(() => {
    const startIdx = events.length > 2 ? 1 : 0;
    requestAnimationFrame(() => scrollToIndex(startIdx, false));
  }, [events.length, scrollToIndex]);

  // Auto-play — advances every AUTO_PLAY_MS, pauses on hover or drag
  useEffect(() => {
    if (totalSlides <= 1) return;
    const id = setInterval(() => {
      if (isHovered.current || isDragging.current) return;
      const next = (activeIndexRef.current + 1) % totalSlides;
      scrollToIndex(next, true);
    }, AUTO_PLAY_MS);
    return () => clearInterval(id);
  }, [totalSlides, scrollToIndex]);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const containerCenter = container.scrollLeft + container.offsetWidth / 2;

    let closestIdx = 0;
    let closestDist = Infinity;

    // Find closest rendered item
    renderItems.forEach((_, idx) => {
      const cardCenter = idx * (cardWidth + gap) + cardWidth / 2;
      const dist = Math.abs(containerCenter - cardCenter);

      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = idx;
      }
    });

    // Convert render index to real index
    const realIdx = closestIdx - itemOffset;

    // Detect if we've scrolled into clone zones and teleport silently
    if (closestIdx < cloneCount) {
      // We're in tail clones zone, teleport to the end of real items
      isJumping.current = true;
      const teleportIdx = totalSlides - cloneCount + closestIdx;
      scrollToIndex(teleportIdx, false);
      isJumping.current = false;
      return;
    }

    if (closestIdx >= itemOffset + totalSlides) {
      // We're in head clones zone, teleport to the start of real items
      isJumping.current = true;
      const teleportIdx = closestIdx - itemOffset - totalSlides;
      scrollToIndex(teleportIdx, false);
      isJumping.current = false;
      return;
    }

    // Normal case: update active index with real index
    setActiveIndex(realIdx);

    // Edge padding logic
    const isAtStart = container.scrollLeft <= 10;
    const isAtEnd =
      container.scrollLeft + container.offsetWidth >=
      container.scrollWidth - 10;

    setShowEdgePadding(isAtStart || isAtEnd);
  }, [
    renderItems,
    cardWidth,
    gap,
    itemOffset,
    totalSlides,
    cloneCount,
    scrollToIndex,
  ]);

  return (
    <div
      className="w-full"
      onMouseEnter={() => { isHovered.current = true; }}
      onMouseLeave={() => { isHovered.current = false; }}
    >
      {/* Slider container with fade masks */}
      <div className="relative">
        {/* Left fade */}
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-neutral-60 to-transparent" />

        {/* Right fade */}
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-neutral-60 to-transparent" />

        <div
          ref={sliderRef}
          className={`flex overflow-x-auto scrollbar-hide py-4 transition-all duration-300 ${
            showEdgePadding ? "px-12" : "px-0"
          }`}
          style={{ gap, cursor: "grab" }}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {renderItems.map((event, idx) => {
            // Real index for click logic
            const realIdx = idx - itemOffset;
            const normalizedIdx =
              ((realIdx % totalSlides) + totalSlides) % totalSlides;
            const isActive = normalizedIdx === activeIndex;

            return (
              <div
                key={`${event.id}-${idx}`}
                className="transition-all duration-300 flex-shrink-0"
                style={{
                  opacity: isActive ? 1 : 0.5,
                  transform: isActive ? "scale(1)" : "scale(0.95)",
                }}
                onClick={() => {
                  // Ignore click if it was a drag gesture
                  if (dragMoved.current) return;
                  if (isActive) {
                    const eventKey = event.slug?.trim() || event.id;
                    navigate(`/events/${encodeURIComponent(eventKey)}`);
                  } else {
                    scrollToIndex(normalizedIdx, true);
                  }
                }}
              >
                <EventCardPrimary event={event} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dot indicators - only show real event count */}
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: totalSlides }).map((_, idx) => (
          <button
            key={idx}
            className={`h-2.5 rounded-full border-none transition-all duration-300 cursor-pointer ${
              idx === activeIndex ? "w-7 bg-secondary-120" : "w-2.5 bg-white/40"
            }`}
            onClick={() => scrollToIndex(idx, true)}
            aria-label={`Ir al evento ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
